//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'

import { LocalizationHelper, IUserSession, EventsNames, CommonHelper, TemplateHelper, ConfigHelper, ToastHelper } from '../../helpers'

import { Enums, Activity } from '../../models/common'
import { OpTaskType } from '../../models/core'

import { GridController, GridTypes, ButtonsOptions } from '../common/grid/grid.controller'
import { ScopeBaseGrid, BaseGridController } from '../common/common.base.grid.controller'

import { TaskTypeHelper } from './tasks-types.helper'
import { SystemService } from '../../services/system'


export interface TaskTypeScope extends ScopeBaseGrid {
    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class TaskTypeController extends BaseGridController {

    // Members

    private grid: GridController<OpTaskType>

    // Members End


    // Ctor

    constructor(
        protected $scope: TaskTypeScope,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        protected systemService: SystemService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSession, systemService, api, localDictionary, null, referenceType)

        this.setEvents()
        this.setMainGridOptions()
        this.setConfirmationsOptions()
    }

    // Ctor End


    // Events

    private onInitialized(e: any, grid: GridController<OpTaskType>): void {
        this.dataGrid = e.component
        this.grid = grid

        this.setGridId(e.tableId)
    }

    private onAdd(): void {
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, TaskTypeHelper.getCreateEditOptions(this.userSession))
    }

    private onEdit(): void {
        if (this.grid.selectedCount == 1) {
            this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, TaskTypeHelper.getCreateEditOptions(this.userSession, this.grid.selectedItemId, this.grid.selectedItem))
        } else {
            ToastHelper.showInfo(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BulkEditIsUnsupported))
        }
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()
    }

    private onConfirmDelete(): void {
        TaskTypeHelper
            .deleteTaskType(this.userSession, this.grid.selectedItem)
            .then(() => {
                this.onDeleted()
            })
    }

    private onShowDetails(e: any): void {
        this.showDetails(e, TaskTypeHelper.getDetailsOptions(this.grid.selectedItemId, this.grid.pageItemsIds))
    }

    // Events End


    // Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = [];

        if (this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSession),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_ADD) ? "['" + Activity[Activity.TASK_TYPE_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_DEL) ? "['" + Activity[Activity.TASK_TYPE_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_EDIT) ? "['" + Activity[Activity.TASK_TYPE_EDIT] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TASK_TYPE_LIST) ? "['" + Activity[Activity.TASK_TYPE_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onEditClick: () => this.onEdit(),
            onDeleteClick: () => this.onDelete(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )

        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    private setMainGridOptions(): void {
        this.$scope.mainGridOptions = {
            addCustomFilters: true,
            buttonsOptions: this.getGridButtonsOptions(),
            columnsDictionary: TaskTypeHelper.getMainGridColumns(),
            idReferenceType: this.referenceType,
            localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.taskTypeSaved,
            name: 'TasksTypesGrid',
            preExecuteClearFilter: this.preExecuteGridClearFilter,
            referenceId: this.referenceType,
            type: GridTypes.MAIN,

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.TaskTypeModuleMainGrid),

            getDataSource: () => this.userSession
                .getDataProvider().TaskType
                .getTaskTypeFilter([], 0, 0, null)
                .then((result: any) => result.objects),

            contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
            onInitialized: (e: any, grid: GridController<OpTaskType>) => this.onInitialized(e, grid),
            onDataReady: (data: any) => this.onDataReady(data),
            onRowDoubleClick: (e: any) => this.onShowDetails(e)
        }
    }

    // Setup End
}